import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Session } from '@feathr/blackbox';
import { showError } from '@feathr/components';

interface IUseImpersonationNavNoticeReturn {
  /** Whether the active session is an impersonated one (JWT carries `impersonator_id`). */
  isImpersonating: boolean;
  /** Whether a return is in flight; the control is disabled until the page navigates away. */
  isReturning: boolean;
  /** Return to the original sudoer session (navigates via the exchange flow). */
  returnToSession: () => Promise<void>;
}

/**
 * State + return action for the nav impersonation notice. Detection is read from the active JWT
 * client-side; the return performs a full-navigation exchange back to the sudoer session.
 *
 * `isReturning` latches on success rather than resetting: `/session/restore` consumes the sudo
 * stash single-use, so a repeat click before the navigation lands would 400. It is cleared only on
 * failure, so the user can retry after the error.
 */
export function useImpersonationNavNotice(): IUseImpersonationNavNoticeReturn {
  const { t } = useTranslation();
  const [isReturning, setIsReturning] = useState(false);

  const returnToSession = useCallback(async (): Promise<void> => {
    setIsReturning(true);
    try {
      await Session.restore();
    } catch (error) {
      setIsReturning(false);
      showError(error, t);
    }
  }, [t]);

  return {
    isImpersonating: Session.isImpersonating,
    isReturning,
    returnToSession,
  };
}
